import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Lightbulb, CheckCircle, XCircle, RefreshCw, AlertTriangle, Users, Home, UserCircle } from 'lucide-react';

function PriorityBadge({ priority }) {
  const variants = {
    HIGH: 'bg-red-500/10 text-red-600 border-red-500/20',
    MEDIUM: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    LOW: 'bg-green-500/10 text-green-600 border-green-500/20',
  };
  return (
    <Badge variant="outline" className={variants[priority] || 'bg-muted'}>
      {priority}
    </Badge>
  );
}

function TypeBadge({ type }) {
  const variants = {
    FUNDING: 'bg-blue-500/10 text-blue-600',
    COMPLIANCE: 'bg-purple-500/10 text-purple-600',
    CARE: 'bg-pink-500/10 text-pink-600',
    STAFFING: 'bg-cyan-500/10 text-cyan-600',
    RISK: 'bg-red-500/10 text-red-600',
    GOAL: 'bg-green-500/10 text-green-600',
    OPERATIONAL: 'bg-orange-500/10 text-orange-600',
  };
  return (
    <Badge variant="secondary" className={variants[type] || 'bg-muted'}>
      {type}
    </Badge>
  );
}

function EntityIcon({ entityType }) {
  switch (entityType) {
    case 'PARTICIPANT': return <Users className="h-4 w-4" />;
    case 'STAFF': return <UserCircle className="h-4 w-4" />;
    case 'HOUSE': return <Home className="h-4 w-4" />;
    default: return <AlertTriangle className="h-4 w-4" />;
  }
}

function RecommendationCard({ recommendation, onResolve, onDismiss }) {
  const { id, message, priority, recommendationType, entityType, entityName, status } = recommendation;

  return (
    <Card 
      className={`transition-colors ${status === 'DISMISSED' ? 'opacity-50' : ''}`}
      data-testid={`recommendation-card-${id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            priority === 'HIGH' ? 'bg-red-500/10' :
            priority === 'MEDIUM' ? 'bg-amber-500/10' : 'bg-green-500/10'
          }`}>
            <Lightbulb className={`h-5 w-5 ${
              priority === 'HIGH' ? 'text-red-500' :
              priority === 'MEDIUM' ? 'text-amber-500' : 'text-green-500'
            }`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <PriorityBadge priority={priority} />
              <TypeBadge type={recommendationType} />
              {entityType && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <EntityIcon entityType={entityType} />
                  <span>{entityName || entityType}</span>
                </div>
              )}
            </div>
            <p className="text-sm">{message}</p>
          </div>
          {status === 'ACTIVE' && (
            <div className="flex flex-col gap-1 flex-shrink-0">
              <Button 
                size="sm" 
                onClick={() => onResolve(id)}
                className="w-full"
                data-testid={`resolve-btn-${id}`}
              >
                <CheckCircle className="h-3 w-3 mr-1" />
                Resolve
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => onDismiss(id)}
                data-testid={`dismiss-btn-${id}`}
              >
                <XCircle className="h-3 w-3 mr-1" />
                Dismiss
              </Button>
            </div>
          )}
          {status === 'RESOLVED' && (
            <Badge variant="outline" className="bg-green-500/10 text-green-600">
              Resolved
            </Badge>
          )}
          {status === 'DISMISSED' && (
            <Badge variant="outline" className="bg-gray-500/10 text-gray-600">
              Dismissed
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCard({ title, value, icon: Icon, variant = 'default' }) {
  const variants = {
    default: 'bg-card',
    danger: 'bg-red-500/5 border-red-500/20',
    warning: 'bg-amber-500/5 border-amber-500/20',
    success: 'bg-green-500/5 border-green-500/20',
  };
  return (
    <Card className={variants[variant]}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          {Icon && (
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              variant === 'danger' ? 'bg-red-500/10' :
              variant === 'warning' ? 'bg-amber-500/10' :
              variant === 'success' ? 'bg-green-500/10' : 'bg-primary/10'
            }`}>
              <Icon className={`h-5 w-5 ${
                variant === 'danger' ? 'text-red-500' :
                variant === 'warning' ? 'text-amber-500' :
                variant === 'success' ? 'text-green-500' : 'text-primary'
              }`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function RecommendationsPage() {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('ACTIVE');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/recommendations', { params: { pageSize: 100 } });
      setRecommendations(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.get('/recommendations/generate');
      toast.success('Recommendations generated');
      fetchData();
    } catch (err) {
      toast.error('Failed to generate recommendations');
    } finally {
      setGenerating(false);
    }
  };

  const handleResolve = async (id) => {
    try {
      await api.post(`/recommendations/${id}/resolve`);
      toast.success('Recommendation resolved');
      fetchData();
    } catch (err) {
      toast.error('Failed to resolve recommendation');
    }
  };

  const handleDismiss = async (id) => {
    try {
      await api.post(`/recommendations/${id}/dismiss`);
      toast.success('Recommendation dismissed');
      fetchData();
    } catch (err) {
      toast.error('Failed to dismiss recommendation');
    }
  };

  const filtered = recommendations.filter(r => {
    const matchPriority = priorityFilter === 'all' || r.priority === priorityFilter;
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchPriority && matchStatus;
  });

  const activeCount = recommendations.filter(r => r.status === 'ACTIVE').length;
  const highCount = recommendations.filter(r => r.priority === 'HIGH' && r.status === 'ACTIVE').length;
  const mediumCount = recommendations.filter(r => r.priority === 'MEDIUM' && r.status === 'ACTIVE').length;

  return (
    <div className="space-y-6" data-testid="recommendations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Recommendations</h1>
          <p className="text-muted-foreground">AI-powered actionable recommendations</p>
        </div>
        <Button onClick={handleGenerate} disabled={generating} data-testid="generate-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
          Generate New
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          title="Active"
          value={activeCount}
          icon={Lightbulb}
        />
        <KpiCard
          title="High Priority"
          value={highCount}
          icon={AlertTriangle}
          variant="danger"
        />
        <KpiCard
          title="Medium Priority"
          value={mediumCount}
          icon={AlertTriangle}
          variant="warning"
        />
        <KpiCard
          title="Total"
          value={recommendations.length}
          icon={Lightbulb}
          variant="success"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-40" data-testid="priority-filter">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="RESOLVED">Resolved</SelectItem>
                <SelectItem value="DISMISSED">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No recommendations found
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <RecommendationCard
              key={r.id}
              recommendation={r}
              onResolve={handleResolve}
              onDismiss={handleDismiss}
            />
          ))}
        </div>
      )}
    </div>
  );
}
